import React, { useCallback, useEffect, useState } from 'react';
import CustomHeader from '../../components/header/index.jsx';
import url from '../../config.js';
import './cloudaccounts.scss';

const API = `${url.backendUrl}/api/v1/external-cloud-accounts`;
const token = () => localStorage.getItem('token');

async function request(path = '', options = {}) {
  const response = await fetch(`${API}${path}`, {
    ...options,
    headers: { 'x-access-token': token(), 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || `Request failed (${response.status})`);
  return result;
}

function downloadConfig(account) {
  const content = [
    `AWS_ACCOUNT_ID=${account.awsAccountId}`,
    `TOKEN_AUDIENCE=${account.tokenAudience}`,
    `ALLOWED_REGIONS=${account.allowedRegions.join(',')}`,
    `ENVIRONMENT=${account.environment}`,
    '',
  ].join('\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
  link.download = `intelog-aws-${account.integrationId}.env`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function CloudAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ awsAccountId: '', environment: 'production', allowedRegions: 'eu-west-1' });
  const [roles, setRoles] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try { setAccounts(await request()); } catch (error) { setMessage(error.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const begin = async (event) => {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      await request('/onboarding', { method: 'POST', body: JSON.stringify({
        ...form,
        allowedRegions: form.allowedRegions.split(',').map((v) => v.trim()).filter(Boolean),
      }) });
      setForm({ awsAccountId: '', environment: 'production', allowedRegions: 'eu-west-1' });
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const complete = async (account) => {
    const roleArn = (roles[account.id] || '').trim();
    if (!roleArn) return setMessage('Enter the CloudFormation role ARN first.');
    setBusy(true); setMessage('');
    try {
      await request(`/${account.id}/complete`, { method: 'POST', body: JSON.stringify({ roleArn, tokenAudience: account.tokenAudience }) });
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  return <div className="root">
    <CustomHeader />
    <main className="content">
    <div className="ca-page">
      <div className="ca-heading">
        <div><span>AWS onboarding</span><h1>Cloud Accounts</h1><p>Connect customer AWS accounts without storing access keys.</p></div>
      </div>
      {message && <div className="ca-message">{message}</div>}
      <section className="ca-card">
        <h2>Connect AWS account</h2>
        <form className="ca-form" onSubmit={begin}>
          <label>AWS account ID<input required pattern="[0-9]{12}" value={form.awsAccountId} onChange={(e) => setForm({ ...form, awsAccountId: e.target.value })} placeholder="123456789012" /></label>
          <label>Environment<select value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })}><option value="development">Development</option><option value="staging">Staging</option><option value="production">Production</option></select></label>
          <label>Approved regions<input required value={form.allowedRegions} onChange={(e) => setForm({ ...form, allowedRegions: e.target.value })} placeholder="eu-west-1, us-east-1" /></label>
          <button disabled={busy}>Generate onboarding configuration</button>
        </form>
      </section>
      <section className="ca-list">
        {accounts.length === 0 && <div className="ca-card">No AWS cloud accounts connected yet.</div>}
        {accounts.map((account) => <article className="ca-card" key={account.id}>
          <div className="ca-row"><div><h2>{account.awsAccountId}</h2><small>{account.environment} · {account.allowedRegions.join(', ')}</small></div><strong className={`ca-status ca-${account.status}`}>{account.status.replaceAll('_', ' ')}</strong></div>
          <dl><dt>Token audience</dt><dd>{account.tokenAudience}</dd>{account.roleArn && <><dt>Role ARN</dt><dd>{account.roleArn}</dd></>}</dl>
          {account.status === 'pending_customer_setup' && <div className="ca-actions">
            <button type="button" onClick={() => downloadConfig(account)}>Download customer config</button>
            <a href="https://github.com/IntelogAI/aws-cloud-runner-onboarding-package" target="_blank" rel="noreferrer">Open onboarding package</a>
            <input value={roles[account.id] || ''} onChange={(e) => setRoles({ ...roles, [account.id]: e.target.value })} placeholder="Paste CloudFormation RoleArn output" />
            <button type="button" disabled={busy} onClick={() => complete(account)}>Submit for verification</button>
          </div>}
          {account.status === 'pending_verification' && <p className="ca-note">Configuration received. Intelog will activate this account only after its federated role is verified.</p>}
        </article>)}
      </section>
    </div>
    </main>
  </div>;
}
